<template>
<Teleport to="#modal-portal-target">
<div v-if="$store.getters['common/modalName'] === 'editreservation'">
    <div class="modal-header">
        <h3 class="modal-title">
            <template v-if="edit.type === 1">
                <span>
                    {{ $_locale('reserved') }}:
                    <a :href="personLink" target="_blank">{{ edit.firstName }} {{ edit.lastName }}</a>
                </span>
            </template>
            <template v-else>
                <span>{{ edit.originalInfo || edit.info }}</span>
            </template>
        </h3>
    </div>
    <div class="modal-body reservation-modal">
        <div class="row">
            <template v-if="edit.disabled">
                <div class="col-xs-12">
                    <div class="form-group">
                        <label>{{ $_locale('reason') }}</label>
                        <input class="form-control" 
                            type="text" 
                            v-model="edit.disableReason" />
                    </div>
                </div>
            </template>
            <template v-else>
                
                <div class="col-xs-12">
                    <div class="form-group" style="margin-bottom: 10px;">
                        <label>{{ $_locale('reservations.reservationNameTitle') }}</label>
                        <input class="form-control" 
                            type="text" 
                            v-model="edit.overrideName" />
                    </div>
                </div>

                <div class="col-xs-12" v-if="edit.isRecurring">
                    <div class="form-group">
                        <input id="lockOverrideName"
                            type="checkbox" 
                            v-model="edit.lockOverrideName" />
                        <label for="lockOverrideName" style="margin-left: 5px;">{{ $_locale('reservations.reservationNameLockTitle') }}</label>                        
                    </div>
                </div>
                
                <div class="col-xs-12">
                    <p>{{ $_locale('order number') }}
                        <a :href="orderLinkUrl(edit.orderId)" target="_blank">
                            {{ edit.orderId }}
                        </a>
                    </p>
                </div>
            </template>
        </div>
        <div class="row">
            <div class="col-xs-12">
                <div class="form-group">
                    <select class="form-control" 
                        v-model="edit.resourceId"
                        @change="onResourceChange">
                        <option v-for="(resource, index) in settings.resources" 
                            :key="index"
                            :value="resource.resourceId">
                            {{ resource.resourceName }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-xs-12 col-sm-4">
                <label>{{ $_locale('date') }}</label>
                <input type="text" class="form-control" ref="dateInput" />
            </div>
            <div class="col-xs-6 col-sm-4">
                <div class="form-group">
                    <label>{{ $_locale('start time') }}</label>
                    <input class="form-control time-picker" 
                           type="time"
                           v-model="edit.startTime" />
                </div>
            </div>
            <div class="col-xs-6 col-sm-4">
                <div class="form-group">
                    <label>{{ $_locale('end time') }}</label>
                    <input class="form-control time-picker"
                           type="time"
                           v-model="edit.endTime" />
                </div>
            </div>
        </div>
        <template v-if="!edit.disabled">
            <div class="row">
                <div class="col-xs-12">
                    <div class="form-group">
                        <div class="input-group">
                            <input class="form-control" 
                            v-model="edit.totalPrice"
                            type="text" 
                            autocomplete="off"
                            :disabled="isPaidFor"
                            style="text-align:right;">
                            <span class="input-group-addon">{{ $_CURRENCY_SYMBOL }}</span>
                        </div>
                        <div v-if="isPaidFor" style="text-align:right;font-size: 75%;">{{ $_locale('timeblock_legend_reservation_paid') }}</div>
                    </div>
                </div>
            </div>
            <div class="row" v-if="additionalResources.length > 0">
                <div class="col-xs-12">
                    <!--
                    <h4>{{ $_locale('additional resources') }}</h4>
                    -->
                    <table class="table">
                        <tr>
                            <th>{{ $_locale('resource') }}</th>
                            <th style="text-align: right;">{{ $_locale('quantity') }}</th>
                            <th style="text-align: right;">{{ $_locale('price') }}</th>
                        </tr>
                        <tr v-for="row in additionalResources" 
                            :key="row.orderProductId">
                            <td>{{ row.productName }}</td>
                            <td style="text-align: right;">{{ $_formatCurrency(row.quantity, 0) }} {{ $_locale('pcs', false) }}</td>
                            <td style="text-align: right;">{{ $_formatCurrency(row.totalPrice) }} {{ $_CURRENCY_SYMBOL }}</td>
                        </tr>
                    </table>
                </div>
            </div>
        </template>
    </div>
    <div class="modal-footer">
        <button class="btn btn-default pull-left" @click="cancelAction">
            {{ $_locale(activeReservation.placeholder ? 'close' : 'cancel') }}
        </button>

        <template v-if="settings.enableResell && editReservation.reservation.isSellable">
            <button @click="setReservationSaleMode(editReservation.reservation.reservationTimeId, 'onSale')"
                v-if="[2,3].includes(editReservation.reservation.status)" 
                class="btn btn-info">{{ $_locale('put up for sale') }}</button>
            <button @click="setReservationSaleMode(editReservation.reservation.reservationTimeId, 'offMarket')"
                v-if="editReservation.reservation.status == 6" 
                class="btn btn-info">{{ $_locale('remove from market') }}</button>
        </template>
        
        <button class="btn btn-danger" @click="deactivateReservationTime">{{ $_locale('delete') }}</button>
        <button class="btn btn-success" @click="saveEditedReservation">
            <span>{{ $_locale('save') }}</span>
        </button>
    </div>
</div>
</Teleport>
</template>

<script>
import { mapGetters } from 'vuex'
import { durationToBlockCount } from '../common/ReservationService.js'

export default {
    data: () => ({
        isPaidFor: false,
        edit: {},
    }),
    computed: {
        ...mapGetters({
            adminMode: 'common/adminMode',
            editReservation: 'calendar/editReservation',
            activeReservation: 'calendar/activeReservation',
            orderLinkUrl: 'ecom/orderLinkUrl',
            product: 'ecom/product',
            date: 'reservations/date',
            settings: 'reservations/settings',
            service: 'reservations/service',
            reservationsAdditionalResources: 'reservations/reservationsAdditionalResources',
            mobile: 'common/mobile',
        }),
        personLink() {
            const { ADMIN_URL } = window.wisenetwork
            const { personId } = this.edit

            return `${ ADMIN_URL }?action=editperson&personid=${ personId }`
        },
        additionalResources() {
            const { orderId } = this.edit

            return this.reservationsAdditionalResources(row => row.orderId === orderId)
        },
    },
    watch: {
        'editReservation.timestamp': {
            handler(to, from) {
                if(this.$store.getters['common/modalName'] !== 'editreservation') return;
                
                this.$nextTick(() => this.init())
            }
        },
    },
    methods: {
        init() {
            const { block, reservation, orderProductId } = this.editReservation;
            const { personId, firstName, lastName } = reservation;
            const formatCurrency = this.$_formatCurrency;
            
            this.edit = {
                type: block.type,
                info: block.info,
                originalInfo: block.originalInfo,
                disabled: block.disabled,
                disableReason: block.disabled ? block.disableReason : block.extraInformation,
                isRecurring: block.isRecurring,
                overrideName: block.overrideName,
                orderId: reservation.orderId,
                orderProductId: reservation.orderProductId,
                reservationTimeId: reservation.reservationTimeId,
                resourceId: reservation.resourceId.toString(),
                date: moment(reservation.start),
                startTime: moment(reservation.start).format('HH:mm'),
                endTime: moment(reservation.end).format('HH:mm'),
                totalPrice: formatCurrency(reservation.totalPrice),
                taxRate: reservation.taxRate,
                firstName,
                lastName,
                personId,
            }

            if (block.isRecurring) {
                this.edit.lockOverrideName = block.lockOverrideName || false
            }
            
            this.isPaidFor = reservation.orderStatusId === 2;
            //console.log('editReservation.orderProductId', this.edit);
            const { daterangepicker } = window.wisenetwork
            const input = this.$refs.dateInput;

            if (this.mobile) {
                input.readOnly = true;
            }
            
            daterangepicker(
                input,
                {
                    locale: { 
                        format: 'dd DD.MM.YYYY',
                        daysOfWeek: _localeDates.dayNamesMin,
                        monthNames: _localeDates.monthNamesShort,
                        firstDay: 1,
                    },
                    opens: 'right',
                    showWeekNumbers: true,
                    singleDatePicker: true,
                    showDropdowns: true,
                    startDate: this.edit.date,
                }, 
                (startDate, endDate) => {
                    this.edit.date = startDate;
                }
            );
        },
        onResourceChange() {
            const resource = this.settings.resources.filter(row => row.resourceId == this.edit.resourceId)[0]
            
            this.edit.resourceName = resource.resourceName;
        },
        toggleActive(name, value) {
            this.visible = value ? name : 'reservation';
        },
        saveEditedReservation() {
            const EcomService = this.$store.getters['ecom/EcomService'];
            const service = this.service;
            const formatCurrency = this.$_formatCurrency;
            const baseDate = moment(this.edit.date).clone();
            const startTime = this.edit.startTime.split(':');
            const endTime = this.edit.endTime.split(':');
            const dateTimeStart = baseDate.clone();
            const dateTimeEnd = baseDate.clone();

            dateTimeStart.set({ hour: startTime[0], minute: startTime[1], second: 0, millisecond: 0 });
            dateTimeEnd.set({ hour: endTime[0], minute: endTime[1], second: 0, millisecond: 0 });

            if (dateTimeEnd < dateTimeStart) {
                dateTimeEnd.add(24, 'hours')
            }

            const quantity = durationToBlockCount(
                dateTimeStart, 
                dateTimeEnd, 
                service.getBlockTotalLength()
            )
            const totalPrice = formatCurrency(this.edit.totalPrice, false);
            const price = totalPrice / quantity;
            const totalPriceTax = quantity * (price - (price / ((this.edit.taxRate / 100)+1)));
            const data = {
                orderId: this.edit.orderId,
                orderProductId: this.edit.orderProductId,
                reservationTimeId: this.edit.reservationTimeId,
                dateTimeStart: dateTimeStart.format('YYYY-MM-DD HH:mm:ss'),
                dateTimeEnd: dateTimeEnd.format('YYYY-MM-DD HH:mm:ss'),
                resourceId: -(-this.edit.resourceId),
                totalPrice,
                totalPriceTax,
                quantity,
                price,
                disableReason: this.edit.disableReason,
                overrideName: this.edit.overrideName,
            }

            if (this.edit.isRecurring) {
                data.lockOverrideName = this.edit.lockOverrideName
            }
            console.log('saveEditedReservation', data)
            
            this.$store.commit('common/setModal', null);
            
            EcomService.editReservationTime(data)
            .then((response) => {
                const data = response.data;

                if (data.success) {
                    this.$store.dispatch('reservations/onDateChange', this.date)

                    this.$_swal({ icon: 'success', title: _locale('reservation saved').ucfirst() })
                } else {
                    this.$_swal({ icon: 'error', title: data.message })
                }
            })
        },
        deactivateReservationTime() {
            const EcomService = this.$store.getters['ecom/EcomService'];
            
            this.$_swal({
                title: _locale('are you sure').ucfirst(),
                text: '',
                type: 'warning',
                showCancelButton: true,
                confirmButtonClass: 'btn-warning',
                confirmButtonText: _locale('yes').ucfirst(),
                cancelButtonText: _locale('no').ucfirst()
            })
            .then((result) => {
                if (result.isConfirmed) {
                    EcomService.deactivateReservationTime(this.edit.reservationTimeId)
                        .then((response) => {
                            const data = response.data;

                            if (data.success) {
                                this.$store.commit('common/setModal', null);
                                this.$store.dispatch('reservations/onDateChange', this.date);
                                
                                this.$_swal({ icon: 'success', title: _locale('deleted').ucfirst() })
                            } else {
                                this.$_swal({ icon: 'error', title: data.message })
                            }
                        })
                }
            })
        },
        setReservationSaleMode(reservationTimeId, mode) {
            const EcomService = this.$store.getters['ecom/EcomService'];
            this.$store.commit('common/setModal', null);

            EcomService.setReservationSaleMode({
                productId: this.product.productId,
                mode,
                reservationTimeId
            })
            .then((response) => {
                const { data } = response;
                
                if (data.success) {
                    this.$store.dispatch('reservations/onDateChange', this.date);
                } else {
                    this.$_swal({ icon: 'error', title: data.message })
                }
            })
        },
        cancelAction() {
            if (this.activeReservation.placeholder) {
                this.$store.commit('common/setModal', null)
            } else {
                this.$store.commit('common/setModal', 'reservation')
            }
        }
    },
}
</script>